import { CaretDownIcon } from '@phosphor-icons/react';
import { Trans, useTranslation } from 'react-i18next';
import { Link } from 'react-router';

import { useConfig } from '@entities/config/api';
import { ROUTES } from '@shared/config/routes';
import { money } from '@shared/lib/money';
import { percentFromBps } from '@shared/lib/percent';
import { Container, Skeleton, StateBlock } from '@shared/ui';
import section from '@shared/ui/PublicSection/publicSection.module.css';

import styles from './FaqPage.module.css';

/**
 * Вопросы в порядке заказчика (тексты от 29.10.2026). Номера рисует `<ol>`,
 * а не текст: в присланном списке девятого вопроса не было, и номер в строке
 * разошёлся бы с позицией.
 */
const QUESTIONS = [
  'what',
  'buyFrom',
  'dealer',
  'selection',
  'top100',
  'price',
  'afterPayment',
  'condition',
  'suspicious',
  'inspection',
  'negotiation',
  'fee',
  'noDiscount',
  'noPurchase',
  'withdrawal',
  'data',
  'ai',
  'language',
  'afterPurchase',
] as const;

/**
 * Частые вопросы `/faq` — ссылка в подвале.
 *
 * **Цена, вознаграждение и срок отзыва не зашиты в текст.** Они приходят из
 * `GET /config`, как в AGB: хардкод цены в интерфейсе и локалях запрещён
 * (MVP_TECH_RFC §25), а срок отзыва обязан совпадать с Widerrufsbelehrung.
 * Поэтому до ответа конфигурации стоит скелетон, а при её недоступности —
 * честное «не удалось загрузить», а не ответы с пропущенными суммами.
 *
 * Аккордеон — нативный `<details>`: открывается клавиатурой без скриптов, а
 * поиск по странице браузер раскрывает сам.
 */
export function FaqPage() {
  const { t, i18n } = useTranslation();
  const locale = i18n.resolvedLanguage ?? 'de';
  const { data: config, isPending } = useConfig();

  return (
    <Container width="narrow" className={styles.page}>
      <header className={styles.header}>
        <span className={section.eyebrow}>{t('faq.eyebrow')}</span>
        <h1 className={section.title}>{t('faq.title')}</h1>
      </header>

      {config ? (
        <FaqList
          values={{
            price: money(config.payments.price_cents, config.payments.currency, locale),
            flat: money(config.success_fee.flat_cents, config.payments.currency, locale),
            rate: percentFromBps(config.success_fee.rate_bps, locale),
            days: config.legal.withdrawal_period_days,
            action: t('footer.withdrawalAction'),
          }}
        />
      ) : isPending ? (
        <Skeleton height="24rem" radius="var(--radius-card)" />
      ) : (
        <StateBlock tone="error" title={t('faq.loadError')} description={t('faq.loadErrorText')} />
      )}
    </Container>
  );
}

function FaqList({ values }: { values: Record<string, string | number> }) {
  const { t } = useTranslation();

  return (
    <ol className={styles.list}>
      {QUESTIONS.map((key, index) => (
        <li key={key} className={styles.item}>
          <details className={styles.details}>
            <summary className={styles.question}>
              <span className={`${styles.number} tabular`} aria-hidden="true">
                {index + 1}
              </span>
              <span className={styles.questionText}>{t(`faq.items.${key}.question`, values)}</span>
              <CaretDownIcon size={18} weight="bold" aria-hidden="true" className={styles.caret} />
            </summary>
            <p className={styles.answer}>
              <Trans
                i18nKey={`faq.items.${key}.answer`}
                values={values}
                components={{
                  form: <Link to={ROUTES.withdrawalForm} />,
                  info: <Link to={ROUTES.withdrawal} />,
                  privacy: <Link to={ROUTES.privacy} />,
                }}
              />
            </p>
          </details>
        </li>
      ))}
    </ol>
  );
}
